/**
 * Subscription programmes are one purchase, not loose cart lines.
 *
 * The flavour picker splits a 12-box plan across up to nine cart lines, one per
 * flavour. Nothing stopped a customer deleting eight of them and keeping the
 * ninth — which left a "12-Week Supply" of a single box, still on the plan's
 * 30% per-unit discount, recurring every 12 weeks. Cheaper per box than buying
 * one outright, forever. Confirmed reaching checkout 11/08.
 *
 * So: any remove inside a programme removes the whole programme, and the per-line
 * quantity steppers come off — the box count belongs to the plan, not the line.
 *
 * Grouped by selling plan name because Recharge clones a plan per product, so the
 * ids differ across flavours while the name ("12-Week Supply") does not.
 *
 * The proper fix is Recharge Bundles, which makes a programme a single parent
 * line and removes the whole class of problem. That needs a parent product per
 * programme, which is a client decision, so this holds the line until then.
 */
(function () {
  const GROUP = '[data-plan-group]';
  /* The cart page's own items and totals, which have to change together when a
     programme is removed.

     These CANNOT be hardcoded as ['cart-items', 'cart-footer']. Shopify namespaces
     section ids by template, so the live DOM carries
     shopify-section-template--27120154247479__cart-items — a lookup for the bare
     name returns null, the script decides it is not on the cart page, and runs the
     drawer branch instead: removal succeeds server-side but the page keeps showing
     the old lines and total, and the drawer flies open over it. Read the ids off
     the DOM so this survives any template id change. */
  function findCartPageSections() {
    const found = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach((el) => {
      const id = el.id.slice('shopify-section-'.length);
      if (/(^|__)(cart-items|cart-footer)$/.test(id)) found.push(id);
    });
    return found;
  }

  /* Shopify returns the whole section wrapper; the theme's own renderer takes the
     inner HTML of .shopify-section, so match that rather than nesting a second
     wrapper inside the first. */
  function swapSection(id, html) {
    if (!html) return;
    const target = document.getElementById('shopify-section-' + id);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    target.innerHTML = parsed ? parsed.innerHTML : html;
  }

  function rowsInGroup(group) {
    return Array.from(document.querySelectorAll(`[data-plan-group="${CSS.escape(group)}"]`));
  }

  function removeGroup(group) {
    /* Keyed by LINE KEY, not variant id. A variant id does not uniquely identify
       a line once selling plans are involved — the same variant can sit in the
       cart both one-time and on a plan — so a variant-keyed update quietly
       matches nothing and the click appears to do nothing at all. */
    const updates = {};
    rowsInGroup(group).forEach((row) => {
      const key = row.dataset.lineKey;
      if (key) updates[key] = 0;
    });
    if (!Object.keys(updates).length) return Promise.resolve(null);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const miniCart = document.querySelector('mini-cart');
    const body = { updates: updates };

    /* The cart page and the drawer are different surfaces and only one of them
       is on screen. Ask for whichever sections are actually present. */
    const pageSections = findCartPageSections();
    const onCartPage = pageSections.length > 0;
    const sections = pageSections.slice();

    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      miniCart.getSectionsToRender().forEach((s) => {
        if (sections.indexOf(s.id) === -1) sections.push(s.id);
      });
    }
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json())
      .then((json) => {
        if (!json || !json.sections) {
          window.location.reload();
          return;
        }

        pageSections.forEach((id) => swapSection(id, json.sections[id]));

        /* renderContents() OPENS the drawer as its last act, which is right after
           a customer adds something and wrong on the cart page — they are already
           looking at their cart. Update the bubble there and leave it shut. */
        if (onCartPage) {
          swapSection('cart-icon-bubble', json.sections['cart-icon-bubble']);
          return;
        }
        if (miniCart && typeof miniCart.renderContents === 'function') {
          miniCart.renderContents(json);
        } else {
          window.location.reload();
        }
      })
      .catch(() => {
        /* Never leave the customer looking at a cart that didn't change. */
        window.location.reload();
      });
  }

  /* Capture phase, on the document, so this runs before CartRemoveButton's own
     click handler — that one removes a single line by index, and indices shift
     as lines disappear, so it cannot do a group. */
  document.addEventListener(
    'click',
    function (e) {
      /* The group header's own control is the primary route. The per-line bins
         are hidden by CSS, but they still exist in the markup and remain
         reachable by keyboard, so they are caught here too rather than left as
         a way to remove one line of a programme. */
      const groupBtn = e.target.closest('[data-programme-remove]');
      if (groupBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        removeGroup(groupBtn.dataset.programmeRemove);
        return;
      }

      const trigger = e.target.closest('cart-remove-button, .delete-product');
      if (!trigger) return;

      const row = trigger.closest(GROUP);
      if (!row) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      removeGroup(row.dataset.planGroup);
    },
    true
  );
})();
